/**
 * The outbox — queued writes waiting for a connection.
 *
 * Design constraints that shaped this:
 *
 * • **Order is preserved.** Entries drain oldest-first and a failure stops the drain
 *   rather than skipping ahead, because "complete reminder X" queued after "create
 *   reminder X" is meaningless if it arrives first.
 *
 * • **A replay is expected, not exceptional.** Every entry carries the UUID that is also
 *   its server-side idempotency key, so a delivery the client never heard back about can
 *   be retried without creating a duplicate.
 *
 * • **The queue belongs to a user.** Entries record who queued them and are only drained
 *   for that same user — otherwise a shift change on a shared tablet would post one
 *   agent's notes under another's name.
 *
 * • **A rejection is not retried forever.** A 4xx means the server will never accept this
 *   payload; it moves to `failed` and waits for a human. Only network and 5xx faults back
 *   off and retry.
 */

import {
  MAX_SYNC_ATTEMPTS,
  OFFLINE_OPERATION_SPECS,
  resolveOfflinePath,
  retryDelayMs,
  type OfflineOperation,
  type OutboxEntry,
} from '@ias/shared';

import { ApiRequestError, http } from '../api/client';
import { idb, STORE_OUTBOX } from './idb';
import { randomUUID } from '@/lib/uuid';

export type OutboxListener = (entries: OutboxEntry[]) => void;

const listeners = new Set<OutboxListener>();
let draining = false;

async function all(): Promise<OutboxEntry[]> {
  const entries = await idb.getAll<OutboxEntry>(STORE_OUTBOX);
  return entries.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
}

async function notify(): Promise<void> {
  const entries = await all();
  listeners.forEach((listener) => listener(entries));
}

export function subscribeToOutbox(listener: OutboxListener): () => void {
  listeners.add(listener);
  void notify();
  return () => listeners.delete(listener);
}

export async function listOutbox(): Promise<OutboxEntry[]> {
  return all();
}

export interface EnqueueInput {
  operation: OfflineOperation;
  targetId?: string;
  payload: Record<string, unknown>;
  summary: string;
  userId: string;
}

/** Queues a write and returns the entry, whose `id` is also the idempotency key. */
export async function enqueue(input: EnqueueInput): Promise<OutboxEntry> {
  const entry: OutboxEntry = {
    id: randomUUID(),
    operation: input.operation,
    targetId: input.targetId,
    payload: input.payload,
    summary: input.summary,
    status: 'pending',
    attempts: 0,
    createdAt: new Date().toISOString(),
    userId: input.userId,
  };

  await idb.put(STORE_OUTBOX, entry);
  await notify();
  return entry;
}

export async function discard(id: string): Promise<void> {
  await idb.delete(STORE_OUTBOX, id);
  await notify();
}

/** Returns a failed entry to the queue so the user can retry it by hand. */
export async function retryEntry(id: string): Promise<void> {
  const entry = await idb.get<OutboxEntry>(STORE_OUTBOX, id);
  if (!entry) return;
  await idb.put(STORE_OUTBOX, { ...entry, status: 'pending', attempts: 0, error: undefined });
  await notify();
}

export async function clearOutbox(): Promise<void> {
  await idb.clear(STORE_OUTBOX);
  await notify();
}

export interface DrainResult {
  synced: number;
  failed: number;
  remaining: number;
}

/**
 * Sends everything queued for `userId`, oldest first.
 *
 * Re-entrancy is guarded: the online event, the periodic retry and a manual "sync now"
 * can all fire at once, and two concurrent drains would deliver the same entry twice.
 * That is survivable thanks to idempotency, but it is still wasted work and confusing
 * to watch.
 */
export async function drainOutbox(
  userId: string,
  onProgress?: (entry: OutboxEntry) => void,
): Promise<DrainResult> {
  if (draining || !navigator.onLine) {
    const pending = (await all()).filter((e) => e.userId === userId && e.status !== 'failed');
    return { synced: 0, failed: 0, remaining: pending.length };
  }

  draining = true;
  let synced = 0;
  let failed = 0;

  try {
    const queue = (await all()).filter((e) => e.userId === userId && e.status === 'pending');

    for (const entry of queue) {
      // The connection can drop mid-drain; stop rather than burning retry budget.
      if (!navigator.onLine) break;

      const spec = OFFLINE_OPERATION_SPECS[entry.operation];
      const attempts = entry.attempts + 1;

      await idb.put(STORE_OUTBOX, { ...entry, status: 'syncing', attempts });
      onProgress?.(entry);
      await notify();

      try {
        const url = resolveOfflinePath(entry.operation, entry.targetId);
        const body =
          spec.idempotent === 'clientRef' ? { ...entry.payload, clientRef: entry.id } : entry.payload;

        await http.request({ url, method: spec.method, data: body });

        await idb.delete(STORE_OUTBOX, entry.id);
        synced += 1;
      } catch (error) {
        const isPermanent =
          error instanceof ApiRequestError &&
          error.status >= 400 &&
          error.status < 500 &&
          // 401 is not the payload's fault — the session lapsed while we were offline.
          error.status !== 401;

        const exhausted = attempts >= MAX_SYNC_ATTEMPTS;

        if (isPermanent || exhausted) {
          await idb.put(STORE_OUTBOX, {
            ...entry,
            status: 'failed',
            attempts,
            lastAttemptAt: new Date().toISOString(),
            error:
              error instanceof ApiRequestError
                ? error.message
                : 'Η αποστολή απέτυχε / Delivery failed',
          });
          failed += 1;
        } else {
          await idb.put(STORE_OUTBOX, {
            ...entry,
            status: 'pending',
            attempts,
            lastAttemptAt: new Date().toISOString(),
          });
          // Ordering matters, so a transient failure stops the run instead of
          // letting later entries overtake the one that stalled.
          break;
        }
      }

      await notify();
    }
  } finally {
    draining = false;
    await notify();
  }

  const remaining = (await all()).filter((e) => e.userId === userId && e.status === 'pending').length;
  return { synced, failed, remaining };
}

export { retryDelayMs };
